// Minimal rich-text model for slide descriptions (bold / italic / a text
// color / line breaks). The editor stores a LIMITED-HTML string in the
// existing `body` text column; this module is the single source of truth for
// what markup is allowed.
//
// Shared between client (editor + live template render) and server (sanitizes
// on save, SSR renders the same markup, receptionist KB needs plain text), so
// it must stay dependency-free and DOM-free: the sanitizer is a whitelist
// tokenizer, not an HTML parser. Any tag that doesn't EXACTLY match the
// whitelist is escaped to visible text — unknown attributes (onload, href,
// class…) therefore can never survive.

// Exact-match tag whitelist. contentEditable emits <b>/<i> (or
// <strong>/<em>), <br>, <div> blocks, and colored text as either
// <span style="color: …"> or legacy <font color="…">.
const SIMPLE_TAGS = /^<\/?(b|strong|i|em|u)>$/i;
const BR_TAG = /^<br\s*\/?>$/i;
const BLOCK_TAGS = /^<\/?(p|div)>$/i;
const LIST_TAGS = /^<\/?(ul|ol|li)>$/i;
const SPAN_OPEN =
  /^<span style="color:\s*(#[0-9a-fA-F]{3}|#[0-9a-fA-F]{6}|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\))\s*;?\s*">$/i;
// A single fixed marker for the owner's accent fade. Never allow user-supplied
// classes, colors, or CSS through this tag; rendering derives the live accent.
const ACCENT_EFFECT_OPEN = /^<span class="rt-accent-effect">$/;
const SPAN_CLOSE = /^<\/span>$/i;
const FONT_CLOSE = /^<\/font>$/i;
// Temporary Tab stops around bare media in the owner editor. Unwrap them on
// save/render; their children still pass through the ordinary article whitelist.
const LEGACY_MEDIA_CONTROL = /^<rt-legacy-media contenteditable="false" tabindex="(?:0|-1)" role="button" aria-label="(?:Image|Video) Media Settings">$|^<\/rt-legacy-media>$/;

// <font> open tags carry a color (execCommand foreColor), a face
// (execCommand fontName — hero headline font styles), a size (execCommand
// fontSize — named text-size steps), or any mix when the browser merges
// nested formatting. Each attribute is validated on its own: color must be a
// hex, face must be an EXACT allowlisted headline family
// (shared/headlineFonts.ts), size must be an EXACT allowlisted step
// (shared/richTextSizes.ts) — anything else fails the whole tag.
import { findHeadlineFontByFamily } from "./headlineFonts";
import { findRichTextSize, isSafeTextSize } from "./richTextSizes";
import { parseYouTubeUrl } from "./youtubeMedia";

function normalizeArticleYouTube(tag: string): string | null {
  const match = /^<iframe src="([^"<>]+)" title="YouTube video" loading="lazy" allowfullscreen=""(?: referrerpolicy="strict-origin-when-cross-origin")?>$/.exec(tag);
  if (!match) return null;
  const video = parseYouTubeUrl(match[1]);
  if (!video || video.embedUrl !== match[1]) return null;
  // YouTube requires an origin Referer (error 153 without it). Override the
  // page's no-referrer policy for this embed only; never expose path/query data.
  return `<iframe src="${video.embedUrl}" title="YouTube video" loading="lazy" allowfullscreen="" referrerpolicy="strict-origin-when-cross-origin">`;
}

const FONT_COLOR_VALUE = /^(#[0-9a-fA-F]{3}|#[0-9a-fA-F]{6})$/;
// Browsers sometimes serialize the color onto the font tag as a style
// attribute instead of (or alongside) color= — e.g.
// <font face="Playfair Display" style="color: rgb(244, 241, 236);"> or a
// leftover empty style="". Allow ONLY a color declaration (or empty).
const FONT_STYLE_VALUE =
  /^\s*(?:color:\s*(?:#[0-9a-fA-F]{3}|#[0-9a-fA-F]{6}|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\))\s*;?\s*)?$/;

// Browsers may merge color and size declarations while editing. Accept only
// the allowlisted Hero sizes, optionally alongside an already-allowed color.
function isSizedInlineStyle(style: string): boolean {
  const declarations = style.split(";").filter((part) => part.trim());
  const seen = new Set<string>();
  for (const declaration of declarations) {
    const match = /^\s*(color|font-size)\s*:\s*(.*?)\s*$/i.exec(declaration);
    if (!match) return false;
    const property = match[1].toLowerCase();
    if (seen.has(property)) return false;
    seen.add(property);
    if (property === "font-size" ? !isSafeTextSize(match[2]) : !FONT_STYLE_VALUE.test(`color: ${match[2]}`)) return false;
  }
  return seen.has("font-size");
}

function isSizedSpan(tag: string): boolean {
  const match = /^<span style="([^"<>]*)">$/i.exec(tag);
  return !!match && isSizedInlineStyle(match[1]);
}
const FONT_OPEN_SHAPE = /^<font((?:\s+(?:color|face|size|style)="[^"<>]*")+)\s*>$/i;
const FONT_ATTR_G = /\s+(color|face|size|style)="([^"<>]*)"/gi;

// Hyperlinks (July 2026): the editor's Link control emits
// <a href="…" target="_blank" rel="noopener noreferrer">. href is validated
// against a strict allowlist — external http(s), mailto:, tel:, or an
// internal path with exactly ONE leading slash (protocol-relative //host and
// backslashes rejected — same safe-link rules as goal step links). target
// and rel are optional but must be exactly the safe values. Anything else
// fails the whole tag and it renders as escaped visible text.
// Inline images (July 2026, email composer only — opt-in via
// sanitizeRichText's allowImages option so slide/public-page rich text stays
// image-free). The editor emits <img src="…"> pointing at our media CDN;
// src must be a bare http(s) URL, alt is optional free text (no quotes/angle
// brackets survive the attr shape), and style is optional but restricted to
// the exact responsive-sizing declaration the editor writes.
const IMG_SHAPE = /^<img((?:\s+(?:src|alt|style)="[^"<>]*")+)\s*\/?>$/i;
const IMG_ATTR_G = /\s+(src|alt|style)="([^"<>]*)"/gi;
const IMG_STYLE_VALUE = /^\s*max-width:\s*100%;?\s*(?:height:\s*auto;?\s*)?$/i;

// Normalize harmless browser-generated article styles without admitting
// arbitrary CSS, attributes, URLs in styles, or event handlers.
function normalizeArticleEditorTag(tag: string): string | null {
  const match = /^<(img|span|p|div|figure|figcaption)((?:\s+[a-z-]+="[^"<>]*")*)\s*\/?>$/i.exec(tag);
  if (!match) return null;
  const name = match[1].toLowerCase();
  if (name === "span" && SPAN_OPEN.test(tag)) return tag;
  const attrs = Array.from(match[2].matchAll(/\s+([a-z-]+)="([^"<>]*)"/gi));
  if (new Set(attrs.map((attr) => attr[1].toLowerCase())).size !== attrs.length) return null;
  const styles: string[] = [];
  const kept: string[] = [];
  for (const attr of attrs) {
    const key = attr[1].toLowerCase();
    const value = attr[2];
    if (key === "contenteditable" && (name === "figure" || name === "figcaption") && value === "false") continue;
    // Figures are keyboard settings controls only in the owner editor. Strip
    // its focus/accessible-control metadata rather than publish or escape it.
    if (name === "figure" && (
      (key === "tabindex" && /^(0|-1)$/.test(value)) ||
      (key === "role" && value === "button") ||
      (key === "aria-label" && /^(Image|Video) Media Settings$/.test(value))
    )) continue;
    if (key === "style") {
      for (const declaration of value.split(";").filter((part) => part.trim())) {
        const css = /^\s*([a-z-]+)\s*:\s*(.*?)\s*$/i.exec(declaration);
        if (!css) return null;
        const property = css[1].toLowerCase();
        const content = css[2];
        if (property === "color" && /^(?:#[0-9a-f]{3}(?:[0-9a-f]{3})?|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\))$/i.test(content)) {
          if (name !== "img") styles.push(`color: ${content};`);
        } else if (property === "font-size" && isSafeTextSize(content)) {
          if (name !== "img") styles.push(`font-size: ${content};`);
        } else if (property === "font-weight" && /^(?:[1-9]00|normal|bold)$/i.test(content) && name !== "img") {
          styles.push(`font-weight: ${content.toLowerCase()};`);
        } else if (property === "text-align" && /^(left|center|right)$/.test(content) && name !== "img") {
          styles.push(`text-align: ${content};`);
        } else if (name === "figure" && property === "width" && /^(?:[1-9]\d|100)%$/.test(content)) {
          // Article media sizing only: bounded percentages, never arbitrary CSS.
          styles.push(`width: ${content};`);
        } else if (name === "img" && ((property === "max-width" && content === "100%") || (property === "height" && content === "auto"))) {
          // Canonical responsive dimensions are written below.
        } else return null;
      }
    } else if (name === "img" && (key === "src" || key === "alt")) {
      kept.push(` ${key}="${value}"`);
    } else if ((name === "figure" || name === "figcaption") && key === "data-align" && /^(left|center|right)$/.test(value)) {
      kept.push(` data-align="${value}"`);
    } else return null;
  }
  if (name === "img") {
    const canonical = `<img${kept.join("")} style="max-width:100%;height:auto;">`;
    return isAllowedImgTag(tag) ? tag : isAllowedImgTag(canonical) ? canonical : null;
  }
  if (styles.length) kept.push(` style="${styles.join(" ")}"`);
  return `<${name}${kept.join("")}>`;
}

/** Owner-invoked recovery, never a background rewrite of saved prose. */
export function restoreArticleFormatting(input: string): string {
  // Browsers discard unmatched closing iframe tags after an escaped opener.
  // Always close a recovered, strictly validated embed immediately so the
  // rest of the article cannot become iframe fallback text.
  const embeds = input.replace(/&lt;iframe\b[\s\S]*?&gt;(?:\s*(?:&lt;\/iframe&gt;|<\/iframe>))?/gi, (encoded) => {
    const opener = encoded.match(/^&lt;iframe\b[\s\S]*?&gt;/i)![0];
    const tag = opener.replace(/&lt;/gi, "<").replace(/&gt;/gi, ">").replace(/&quot;/gi, '"');
    const clean = normalizeArticleYouTube(tag);
    return clean ? `${clean}</iframe>` : encoded;
  });
  return embeds.replace(/&lt;\/?(?:img|span|font|figure|figcaption|p|div)\b[\s\S]*?&gt;/gi, (encoded) => {
    const tag = encoded.replace(/&lt;/gi, "<").replace(/&gt;/gi, ">").replace(/&quot;/gi, '"');
    const clean = sanitizeRichText(tag, { article: true, allowImages: true });
    return clean.startsWith("<") ? clean : encoded;
  });
}

function isAllowedImgTag(tag: string): boolean {
  const m = tag.match(IMG_SHAPE);
  if (!m) return false;
  const seen = new Set<string>();
  let hasSrc = false;
  for (const attr of Array.from(m[1].matchAll(IMG_ATTR_G))) {
    const name = attr[1].toLowerCase();
    if (seen.has(name)) return false;
    seen.add(name);
    if (name === "src") {
      const v = attr[2].trim();
      if (!/^https?:\/\//i.test(v) || /\s/.test(v) || v.includes("\\")) return false;
      hasSrc = true;
    }
    if (name === "style" && !IMG_STYLE_VALUE.test(attr[2])) return false;
  }
  return hasSrc;
}

/** True when the (sanitized) rich text contains an allowed inline image. */
export function richTextHasImage(input: string | null | undefined): boolean {
  const raw = typeof input === "string" ? input : "";
  const m = raw.match(/<img[^>]*>/gi);
  return !!m && m.some((t) => isAllowedImgTag(t));
}

const A_CLOSE = /^<\/a>$/i;
const A_OPEN_SHAPE = /^<a((?:\s+(?:href|target|rel)="[^"<>]*")+)\s*>$/i;
const A_ATTR_G = /\s+(href|target|rel)="([^"<>]*)"/gi;
const A_REL_VALUE = /^noopener noreferrer$|^noreferrer noopener$/;

export function isSafeRichTextHref(href: string): boolean {
  const v = href.trim();
  if (v === "" || /\s/.test(v) || v.includes("\\")) return false;
  if (/^https?:\/\//i.test(v)) return true;
  if (/^(mailto|tel):/i.test(v)) return true;
  // Website rich text can point at a published slide or offer on the same
  // public page. Keep these query/hash forms narrowly constrained; arbitrary
  // relative URLs and hashes remain disallowed.
  if (/^\?(?:slide=\d+|digi=pricing)$/.test(v)) return true;
  if (/^#(?:go-campaign-\d+|class-schedule)$/.test(v)) return true;
  // Internal path: exactly one leading slash ("//host" is protocol-relative).
  if (v.startsWith("/") && !v.startsWith("//")) return true;
  return false;
}

function isAllowedAnchorOpen(tag: string): boolean {
  const m = tag.match(A_OPEN_SHAPE);
  if (!m) return false;
  const seen = new Set<string>();
  let hasHref = false;
  for (const attr of Array.from(m[1].matchAll(A_ATTR_G))) {
    const name = attr[1].toLowerCase();
    if (seen.has(name)) return false;
    seen.add(name);
    if (name === "href") {
      if (!isSafeRichTextHref(attr[2])) return false;
      hasHref = true;
    }
    if (name === "target" && attr[2] !== "_blank") return false;
    if (name === "rel" && !A_REL_VALUE.test(attr[2])) return false;
  }
  return hasHref;
}

function isAllowedFontOpen(tag: string): boolean {
  const m = tag.match(FONT_OPEN_SHAPE);
  if (!m) return false;
  const seen = new Set<string>();
  for (const attr of Array.from(m[1].matchAll(FONT_ATTR_G))) {
    const name = attr[1].toLowerCase();
    if (seen.has(name)) return false;
    seen.add(name);
    if (name === "color" && !FONT_COLOR_VALUE.test(attr[2])) return false;
    if (name === "face" && !findHeadlineFontByFamily(attr[2])) return false;
    if (name === "size" && !findRichTextSize(attr[2])) return false;
    if (name === "style" && !FONT_STYLE_VALUE.test(attr[2]) && !isSizedInlineStyle(attr[2])) return false;
  }
  return true;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Sanitize a slide-description rich-text string down to the allowed markup.
 * Text content is left as-is (it's already entity-encoded by contentEditable /
 * escaped below when a disallowed tag is flattened). Idempotent — running it
 * over already-sanitized output returns the same string.
 */
export type SanitizeRichTextOptions = {
  /** Text-only expandable sections may contain headings, but not article media. */
  allowHeadings?: boolean;
  /** Allow <img src="https://…"> (email bodies only — never slide content). */
  allowImages?: boolean;
  /** Dedicated long-form articles only; other surfaces retain their whitelist. */
  article?: boolean;
};

export function sanitizeRichText(
  input: string | null | undefined,
  opts?: SanitizeRichTextOptions,
): string {
  const raw = typeof input === "string" ? input : "";
  if (raw === "") return "";
  const allowImages = !!opts?.allowImages;
  let out = "";
  let i = 0;
  while (i < raw.length) {
    const lt = raw.indexOf("<", i);
    if (lt === -1) {
      out += raw.slice(i);
      break;
    }
    out += raw.slice(i, lt);
    const gt = raw.indexOf(">", lt);
    if (gt === -1) {
      // Dangling "<" with no closing ">" — escape the rest verbatim.
      out += escapeHtml(raw.slice(lt));
      break;
    }
    const originalTag = raw.slice(lt, gt + 1);
    if (opts?.article && LEGACY_MEDIA_CONTROL.test(originalTag)) {
      i = gt + 1;
      continue;
    }
    const tag = opts?.article ? normalizeArticleYouTube(originalTag) ?? normalizeArticleEditorTag(originalTag) ?? originalTag : originalTag;
    if (
      SIMPLE_TAGS.test(tag) ||
      BR_TAG.test(tag) ||
      BLOCK_TAGS.test(tag) ||
      LIST_TAGS.test(tag) ||
      SPAN_OPEN.test(tag) ||
      isSizedSpan(tag) ||
      ACCENT_EFFECT_OPEN.test(tag) ||
      SPAN_CLOSE.test(tag) ||
      isAllowedFontOpen(tag) ||
      FONT_CLOSE.test(tag) ||
      isAllowedAnchorOpen(tag) ||
      A_CLOSE.test(tag) ||
      (allowImages && isAllowedImgTag(tag))
      || ((opts?.article || opts?.allowHeadings) && /^<\/?(h1|h2|h3)>$/i.test(tag))
      || (opts?.article && /^<video src="https:\/\/[^"<>\s\\]+" controls=""><\/video>$/i.test(tag))
      || (opts?.article && /^<video src="https:\/\/[^"<>\s\\]+" controls="">$/i.test(tag))
      || (opts?.article && /^<\/video>$/i.test(tag))
      || (opts?.article && /^<figure(?: data-align="(?:left|center|right)")?>$/.test(tag))
      || (opts?.article && /^<\/figure>$/.test(tag))
      || (opts?.article && /^<\/?figcaption>$/.test(tag))
      || (opts?.article && /^<(span|p|div|figure|figcaption)\b/.test(tag) && normalizeArticleEditorTag(tag) !== null)
      || (opts?.article && normalizeArticleYouTube(tag) !== null)
      || (opts?.article && tag === "</iframe>")
    ) {
      out += tag;
    } else {
      out += escapeHtml(tag);
    }
    i = gt + 1;
  }
  return out;
}

/**
 * Rich text → plain text: block/line-break tags become newlines, every other
 * tag is dropped, and the handful of entities contentEditable emits are
 * decoded. Used for blank detection, the receptionist KB, and AI rewrite.
 */
export function richTextToPlain(input: string | null | undefined): string {
  const raw = typeof input === "string" ? input : "";
  if (raw === "") return "";
  return raw
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|figure|figcaption)>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&apos;/gi, "'")
    .replace(/&#0?39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** True when the rich-text string carries no visible text at all. */
export function isRichTextBlank(input: string | null | undefined): boolean {
  return richTextToPlain(input) === "";
}

/**
 * Normalize a body value on save: sanitize, and collapse markup-only content
 * ("<p><br></p>") to the empty string so every existing blank predicate
 * (SQL body = '', client sweeps) keeps working unchanged.
 */
export function normalizeRichTextForSave(
  input: string | null | undefined,
  opts?: SanitizeRichTextOptions,
): string {
  const clean = sanitizeRichText(input, opts);
  // An image with no text is still real content (email bodies) — only
  // collapse to "" when there's neither visible text nor an allowed image.
  if (isRichTextBlank(clean) && !(opts?.allowImages && richTextHasImage(clean))) return "";
  return clean;
}

// NOTE (July 2026): the low-contrast color strip guard (stripUnreadableColors)
// was removed at the owner's request — explicit rich-text colors now render
// exactly as picked on every band, even when unreadable against the band bg.

/**
 * Plain text → rich text: escape HTML-significant characters and turn
 * newlines into <br> so legacy plain-text content (email templates, old
 * drafts) round-trips into the rich editor without losing line breaks.
 */
export function plainTextToRichText(input: string | null | undefined): string {
  const raw = typeof input === "string" ? input : "";
  if (raw === "") return "";
  return escapeHtml(raw).replace(/\r\n|\r|\n/g, "<br>");
}

/** True when the string contains any of the allowed rich-text markup. */
export function hasRichTextMarkup(input: string | null | undefined): boolean {
  const raw = typeof input === "string" ? input : "";
  return /<([a-z]|\/)/i.test(raw);
}
